// @flow
import {fromJS, type Map} from 'immutable';
import moment from 'moment';
import {Store, setUpStore} from 'client/framework';
import {RANGES} from 'generic/SelectableDateRange/shared';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';
import {type QueryBuilder, resource} from '@albert-io/json-api-framework/request/builder';
import studentAssignmentsActions from './StudentAssignments.actions';
import sessionStore from 'client/Session/SessionStore';

const STORE_NAME = 'StudentAssignmentsStore';

export class StudentAssignmentsStore extends Store {
  constructor(name: string) {
    super(name);
    this.setInitialData(
      fromJS({
        classroomId: null,
        totalPages: 1,
        dueDateRangeId: RANGES.ALL_TIME,
        dateRangeFilter: null
      })
    );
    this.setInitialData(this.defaultData);
    this._selectableDateRangeStore = setUpStore(
      SelectableDateRangeStore,
      `${STORE_NAME}SelectableDateRange`
    );
    this.handle(studentAssignmentsActions.SET_CLASSROOM_ID, this.setProperty('classroomId'));
    this.handle(
      studentAssignmentsActions.SET_DUE_DATE_RANGE_ID,
      this.setProperty('dueDateRangeId')
    );
  }

  getDueDateRangeId(): string | null {
    return this.readData('dueDateRangeId');
  }

  getDueDateRangeSelection(): Map<string, *> | null {
    return this.getDateRangeDropdownStore().getSelectedItem();
  }

  getClassroomId(): string {
    return this.readData('classroomId');
  }

  // @todo – support masquerade?
  getStudentId(): string {
    return sessionStore.getUserId();
  }

  isReady(): boolean {
    return this.getAssignmentsQuery().isResourcePopulated();
  }

  getAssignmentsQuery(status: string): QueryBuilder {
    const dateRange = this.getDueDateRangeSelection();
    const startDate = dateRange.get('start_date') || 0;
    const endDate = dateRange.get('end_date') || moment().valueOf();
    const studentId = this.getStudentId();
    return (
      resource('assignments_v3')
        .mandarkEndpoint(['assignments_v3'])
        .include('student_classrooms_v1')
        .include('students_v2', studentId)
        .filter({
          students_v1: this.getStudentId(),
          classrooms_v1: this.getClassroomId(),
          assignment_type: 'assignment',
          start_date: {
            less_than_inclusive: 'now'
          },
          included: {
            students_v2: {
              id: studentId
            },
            student_classrooms_v1: {
              students_v2: {
                id: studentId
              }
            }
          }
        })
        .meta({
          context: {
            student: {
              id: studentId
            }
          }
        })
        .withMeta('assignment_v3')
        .filter(
          {
            due_date: {
              greater_than_inclusive: dateRange && moment(startDate).startOf('day').valueOf(),
              less_than_inclusive: dateRange && moment(endDate).endOf('day').valueOf()
            }
          },
          this.getDateRangeStore().hasInput()
        )
        // CLOSED:
        // The student has submitted the assignment
        // OR (the assignment is past due and late submissions are not allowed)
        .filter(
          {
            any_of: [
              {
                'meta.student_submitted': true
              },
              {
                due_date: {
                  less_than: 'now'
                },
                allow_late_submissions: false
              }
            ]
          },
          status === 'closed'
        )
        // OPEN: status for 'open' is not set to make it the default tab when query prop is used
        // Start Date < Now
        // AND the student hasn't submitted
        // AND [(Due date < Now )OR (Due date > Now AND Late submissions allowed)]
        .filter(
          {
            'meta.student_submitted': false,
            any_of: [
              {
                due_date: {
                  greater_than_inclusive: 'now'
                }
              },
              {
                allow_late_submissions: true
              }
            ]
          },
          !status
        )
    );
  }

  getSelectableDateRangeStore(): Store {
    return this._selectableDateRangeStore;
  }

  getSelectableDateRangeStoreName(): string {
    return this.getSelectableDateRangeStore().getName();
  }

  getDateRangeStore(): Store {
    return this._selectableDateRangeStore.getDateRangeStore();
  }

  getDateRangeDropdownStore(): Store {
    return this._selectableDateRangeStore.getSimpleDropdownStore();
  }

  getDateRangeStoreName(): string {
    return this.getDateRangeStore().getName();
  }

  getDateRangeDropdownStoreName(): string {
    return this.getDateRangeDropdownStore().getName();
  }
}

export default new StudentAssignmentsStore(STORE_NAME);
